import {
  resolvePrimeUiApiBaseUrl,
  resolvePrimeUiApiKey,
  resolvePrimeUiProjectConfig,
  resolvePrimeUiTargetProjectRoot,
} from "./lib/project-link-config.js";
import { ProjectSyncService } from "./services/project-sync-service.js";
import { ApiProjectDataProvider } from "./sources/api-provider.js";
import type { ProjectDataProvider } from "./sources/project-data-provider.js";
import type {
  PrimeUiToolContext,
  ProjectSyncSource,
} from "./sources/project-sync-source.js";
import type {
  PrimeUiComponentCandidatesInput,
  PrimeUiComponentPropsValidateInput,
  PrimeUiCreateComponentExportOptions,
  PrimeUiCreateProjectApiPageInput,
  PrimeUiCreateProjectApiVariantInput,
  PrimeUiManagedSearchRuntimeStatus,
  PrimeUiProjectPageDetailsResult,
  PrimeUiSubmitIssueReportInput,
} from "./types.js";

type LazyProjectSyncSourceOptions = {
  env?: NodeJS.ProcessEnv;
  getCwd?: () => string;
  createProvider?: (options: {
    apiKey: string;
    baseUrl?: string;
  }) => ProjectDataProvider;
};

function defaultCreateProvider(options: {
  apiKey: string;
  baseUrl?: string;
}): ProjectDataProvider {
  return new ApiProjectDataProvider(options);
}

function normalizeSlug(slug: string): string {
  const trimmed = slug.trim();
  if (!trimmed || trimmed === "/") {
    return "/";
  }

  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, "") || "/";
}

export class LazyProjectSyncSource implements ProjectSyncSource {
  private readonly env: NodeJS.ProcessEnv;
  private readonly getCwd: () => string;
  private readonly createProvider: (options: {
    apiKey: string;
    baseUrl?: string;
  }) => ProjectDataProvider;

  private stickyProjectRoot?: string;
  private readonly pageDetailsBySlug = new Map<
    string,
    PrimeUiProjectPageDetailsResult
  >();

  constructor(options: LazyProjectSyncSourceOptions = {}) {
    this.env = options.env ?? process.env;
    this.getCwd = options.getCwd ?? (() => process.cwd());
    this.createProvider = options.createProvider ?? defaultCreateProvider;
  }

  private async withService<T>(
    context: PrimeUiToolContext | undefined,
    operation: (service: ProjectSyncService) => Promise<T>,
    options?: {
      requireTargetProjectRoot?: boolean;
    },
  ): Promise<T> {
    const service = await this.createProjectSyncService(context, options);
    return operation(service);
  }

  private async withMutationService<T>(
    context: PrimeUiToolContext | undefined,
    operation: (service: ProjectSyncService) => Promise<T>,
    options?: {
      requireTargetProjectRoot?: boolean;
    },
  ): Promise<T> {
    // Mutating tools must always resolve and validate project config first.
    const service = await this.createProjectSyncService(context, options);
    return operation(service);
  }

  private async createProjectSyncService(
    context?: PrimeUiToolContext,
    options?: {
      requireTargetProjectRoot?: boolean;
    },
  ): Promise<ProjectSyncService> {
    const requireTargetProjectRoot = options?.requireTargetProjectRoot ?? true;
    const resolvedProjectConfig = await resolvePrimeUiProjectConfig(
      {
        cwd: this.getCwd(),
        projectRootFromTool: context?.projectRoot,
        projectRootFromSticky: this.stickyProjectRoot,
        projectRootFromEnv: this.env.PRIMEUI_PROJECT_ROOT,
      },
      {
        requireTargetProjectPath: requireTargetProjectRoot,
      },
    );

    const projectRoot = resolvedProjectConfig.projectRoot;
    const targetProjectRoot = requireTargetProjectRoot
      ? resolvePrimeUiTargetProjectRoot(
          projectRoot,
          resolvedProjectConfig.projectConfig,
        )
      : projectRoot;

    const apiKey = await resolvePrimeUiApiKey({
      projectConfig: resolvedProjectConfig.projectConfig,
      apiKeyFromEnv: this.env.PRIMEUI_API_KEY,
    });
    const baseUrl = resolvePrimeUiApiBaseUrl({
      projectConfig: resolvedProjectConfig.projectConfig,
      baseUrlFromEnv: this.env.PRIMEUI_API_BASE_URL,
    });

    this.stickyProjectRoot = projectRoot;

    const provider = this.createProvider({
      apiKey,
      baseUrl,
    });

    return new ProjectSyncService({
      projectRoot,
      targetProjectRoot,
      provider,
      pageDetailsBySlug: this.pageDetailsBySlug,
    });
  }

  async getProjectInfo(context?: PrimeUiToolContext) {
    return this.withService(context, (service) =>
      service.getProjectInfo(context),
    );
  }

  async getProjectDescription(context?: PrimeUiToolContext) {
    return this.withService(
      context,
      (service) => service.getProjectDescription(context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async upsertProjectDescription(
    projectDescription: string,
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(
      context,
      async (service) => {
        const result = await service.upsertProjectDescription(
          projectDescription,
          context,
        );
        this.pageDetailsBySlug.clear();
        return result;
      },
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async listProjectPages(context?: PrimeUiToolContext) {
    return this.withService(
      context,
      (service) => service.listProjectPages(context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async getProjectPage(pageId: string, context?: PrimeUiToolContext) {
    return this.withService(
      context,
      (service) => service.getProjectPage(pageId, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async createProjectPage(
    input: PrimeUiCreateProjectApiPageInput,
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(
      context,
      async (service) => {
        const result = await service.createProjectPage(input, context);
        this.pageDetailsBySlug.clear();
        return result;
      },
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async listProjectPageVariants(pageId: string, context?: PrimeUiToolContext) {
    return this.withService(
      context,
      (service) => service.listProjectPageVariants(pageId, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async getProjectPageVariant(
    pageId: string,
    variantId: string,
    context?: PrimeUiToolContext,
  ) {
    return this.withService(
      context,
      (service) => service.getProjectPageVariant(pageId, variantId, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async createProjectPageVariant(
    pageId: string,
    input: PrimeUiCreateProjectApiVariantInput,
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(
      context,
      async (service) => {
        const result = await service.createProjectPageVariant(
          pageId,
          input,
          context,
        );
        this.pageDetailsBySlug.clear();
        return result;
      },
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async setProjectPageActiveVariant(
    pageId: string,
    variantId: string,
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(
      context,
      async (service) => {
        const result = await service.setProjectPageActiveVariant(
          pageId,
          variantId,
          context,
        );
        this.pageDetailsBySlug.clear();
        return result;
      },
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async submitIssueReport(
    input: PrimeUiSubmitIssueReportInput,
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(
      context,
      (service) => service.submitIssueReport(input, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async getSearchOverview(context?: PrimeUiToolContext) {
    return this.withService(
      context,
      (service) => service.getSearchOverview(context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async getSearchEnvironment(
    environmentSlug: string,
    context?: PrimeUiToolContext,
  ) {
    return this.withService(
      context,
      (service) => service.getSearchEnvironment(environmentSlug, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async createSearchEnvironment(
    input: {
      name: string;
      slug: string;
    },
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(
      context,
      (service) => service.createSearchEnvironment(input, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async updateSearchEnvironment(
    input: {
      environmentSlug: string;
      name?: string;
      status?: PrimeUiManagedSearchRuntimeStatus;
    },
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(
      context,
      (service) => service.updateSearchEnvironment(input, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async deleteSearchEnvironment(
    environmentSlug: string,
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(
      context,
      (service) => service.deleteSearchEnvironment(environmentSlug, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async syncSearchEnvironment(
    environmentSlug: string,
    context?: PrimeUiToolContext,
  ) {
    return this.withService(context, (service) =>
      service.syncSearchEnvironment(environmentSlug, context),
    );
  }

  async getComponentCandidates(
    input: PrimeUiComponentCandidatesInput,
    context?: PrimeUiToolContext,
  ) {
    return this.withService(
      context,
      (service) => service.getComponentCandidates(input, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async validateComponentProps(
    input: PrimeUiComponentPropsValidateInput,
    context?: PrimeUiToolContext,
  ) {
    return this.withService(
      context,
      (service) => service.validateComponentProps(input, context),
      {
        requireTargetProjectRoot: false,
      },
    );
  }

  async listExports(context?: PrimeUiToolContext) {
    return this.withService(context, (service) => service.listExports(context));
  }

  async createExport(context?: PrimeUiToolContext) {
    return this.withService(context, (service) =>
      service.createExport(context),
    );
  }

  async createComponentExport(
    componentId: string,
    options?: PrimeUiCreateComponentExportOptions,
    context?: PrimeUiToolContext,
  ) {
    return this.withService(context, (service) =>
      service.createComponentExport(componentId, options, context),
    );
  }

  async downloadExportById(id: string, context?: PrimeUiToolContext) {
    return this.withService(context, (service) =>
      service.downloadExportById(id, context),
    );
  }

  async downloadComponentExportById(id: string, context?: PrimeUiToolContext) {
    return this.withService(context, (service) =>
      service.downloadComponentExportById(id, context),
    );
  }

  async inspectPage(slug: string, context?: PrimeUiToolContext) {
    const result = await this.withService(context, (service) =>
      service.inspectPage(slug, context),
    );

    this.pageDetailsBySlug.set(normalizeSlug(slug), result);
    return result;
  }

  async copyPage(originPageSlug: string, context?: PrimeUiToolContext) {
    return this.withMutationService(context, (service) =>
      service.copyPage(originPageSlug, context),
    );
  }

  async copyComponent(
    originPageSlug: string,
    blockId: string,
    componentId: string,
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(context, (service) =>
      service.copyComponent(originPageSlug, blockId, componentId, context),
    );
  }

  async copyRegistryComponent(
    componentId: string,
    context?: PrimeUiToolContext,
  ) {
    return this.withMutationService(context, (service) =>
      service.copyRegistryComponent(componentId, context),
    );
  }

  async copyExportable(exportableKey: string, context?: PrimeUiToolContext) {
    return this.withMutationService(context, (service) =>
      service.copyExportable(exportableKey, context),
    );
  }

  async clearTemp(context?: PrimeUiToolContext) {
    const result = await this.withMutationService(context, (service) =>
      service.clearTemp(context),
    );

    this.pageDetailsBySlug.clear();
    return result;
  }
}
